import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import type { TFunction } from 'i18next'
import { subscriptionsApi } from '../../api/client'
import { FREE_SIGNUP_CREDITS } from '../../config/en/credits'
import { PRODUCT_LOGO_SRC, PRODUCT_NAME } from '../../config/product'
import { useAuthStore } from '../../store/authStore'
import { trackEvent } from '../../utils/productAnalytics'
import { LANDING_EXAMPLES, LANDING_HERO_VIDEO, LANDING_LIB_IMAGES } from './landingMedia'
import {
  applyLandingSeo,
  initLandingAnalytics,
  trackLandingCta,
  useLandingDarkThemeEffect,
} from './landingUtils'
import './LandingPage.css'
import { formatUsd } from '../../utils/formatMoney'
import { subscriptionPlanLabel } from '../../config/en/subscriptionPlans'

const examples = [1, 2, 3, 4, 5, 6]

const libImgs = LANDING_LIB_IMAGES

type LandingPlanCard = {
  key: string
  title: string
  price: string
  desc: string
  accent?: boolean
}

type PlansApiFree = {
  signup_credits?: number
  subscription_credits_monthly?: number
  trial_days?: number
}

type PlansApiPaid = {
  slug: string
  name: string
  price_usd: number
  subscription_credits_monthly: number
  ai_chat_message_limit: number | null
}

function formatCreditsLabel(t: TFunction, count: number): string {
  return t('landing.pricing.credits', { count })
}

function getDefaultLandingPlans(t: TFunction): LandingPlanCard[] {
  return [
    {
      key: 'free',
      title: 'Free',
      price: formatUsd(0),
      desc: t('landing.pricing.freeDesc', {
        creditsLabel: formatCreditsLabel(t, FREE_SIGNUP_CREDITS),
        trialDays: 14,
      }),
    },
    {
      key: 'basic',
      title: t('landing.pricing.basic.title'),
      price: t('landing.pricing.basic.price'),
      desc: t('landing.pricing.basic.desc'),
    },
    {
      key: 'medium',
      title: t('landing.pricing.medium.title'),
      price: t('landing.pricing.medium.price'),
      desc: t('landing.pricing.medium.desc'),
      accent: true,
    },
    {
      key: 'premium',
      title: t('landing.pricing.premium.title'),
      price: t('landing.pricing.premium.price'),
      desc: t('landing.pricing.premium.desc'),
    },
  ]
}

/** Merge API catalog with defaults so empty `plans` does not drop paid cards. */
function mergeLandingPlans(
  t: TFunction,
  free: PlansApiFree,
  apiPlans: PlansApiPaid[],
  defaults: LandingPlanCard[],
  locale: string,
): LandingPlanCard[] {
  const paidBySlug = new Map(apiPlans.map(p => [p.slug, p]))
  const signupCredits = free.signup_credits ?? free.subscription_credits_monthly ?? FREE_SIGNUP_CREDITS
  const freeDesc = t('landing.pricing.freeDesc', {
    creditsLabel: formatCreditsLabel(t, signupCredits),
    trialDays: free.trial_days ?? 14,
  })

  return defaults.map(defaultCard => {
    if (defaultCard.key === 'free') {
      return { ...defaultCard, desc: freeDesc }
    }
    const p = paidBySlug.get(defaultCard.key)
    if (!p) return defaultCard
    const chatLimit = p.ai_chat_message_limit == null
      ? t('landing.pricing.chatUnlimited')
      : t('landing.pricing.chatMessages', { count: p.ai_chat_message_limit })
    return {
      key: p.slug,
      title: subscriptionPlanLabel(p.slug, defaultCard.title),
      price: t('landing.pricing.pricePerMonth', {
        price: formatUsd(p.price_usd, locale),
      }),
      desc: t('landing.pricing.planDesc', {
        credits: p.subscription_credits_monthly,
        chatLimit,
      }),
      accent: p.slug === 'medium',
    }
  })
}

const LANDING_CONTACT_EMAIL = 'inbox@spectrai.pro'

export default function Landing() {
  const { t, i18n } = useTranslation()
  const priceLocale = i18n.language?.startsWith('ru') ? 'ru-RU' : 'en-US'
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const enterGuestMode = useAuthStore((s) => s.enterGuestMode)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const defaultPlans = useMemo(() => getDefaultLandingPlans(t), [t])
  const [landingPlans, setLandingPlans] = useState<LandingPlanCard[]>(defaultPlans)

  const features = useMemo(
    () => [
      { title: t('landing.features.images.title'), desc: t('landing.features.images.desc') },
      { title: t('landing.features.productCards.title'), desc: t('landing.features.productCards.desc') },
      { title: t('landing.features.texts.title'), desc: t('landing.features.texts.desc') },
      { title: t('landing.features.infographics.title'), desc: t('landing.features.infographics.desc') },
    ],
    [t],
  )

  const logoStrip = useMemo(
    () => t('landing.logos.items', { returnObjects: true }) as string[],
    [t],
  )

  const useCases = useMemo(
    () => [
      t('landing.useCases.socialPosts'),
      t('landing.useCases.amazon'),
      t('landing.useCases.banners'),
      t('landing.useCases.brands'),
      t('landing.useCases.productInfographics'),
      t('landing.useCases.aiVisual'),
    ],
    [t],
  )

  const exampleMeta = useMemo(
    () => [
      { title: t('landing.examples.videoAvatar'), desc: t('landing.examples.videoAvatarDesc') },
      { title: t('landing.examples.videoProduct'), desc: t('landing.examples.videoProductDesc') },
      { title: t('landing.examples.productCards'), desc: t('landing.examples.productCardsDesc') },
      { title: t('landing.examples.adVideo'), desc: t('landing.examples.adCreativeDesc') },
      { title: t('landing.examples.adCreatives'), desc: t('landing.examples.adCreativeDesc') },
      { title: t('landing.examples.aiChat'), desc: t('landing.examples.adCreativeDesc') },
    ],
    [t],
  )

  useEffect(() => {
    setLandingPlans(defaultPlans)
  }, [defaultPlans])

  useEffect(() => {
    subscriptionsApi.getPlans()
      .then(res => {
        setLandingPlans(mergeLandingPlans(t, res.data.free, res.data.plans ?? [], defaultPlans, priceLocale))
      })
      .catch(() => {})
  }, [t, defaultPlans, priceLocale])

  const handleRegisterCta = (source: string) => {
    trackLandingCta('landing', source)
    navigate('/register')
  }

  const handleExploreCta = (source: string) => {
    trackLandingCta('landing', source)
    trackEvent('guest_entered', { source })
    enterGuestMode()
    navigate('/home')
  }

  const handlePricingCta = (plan: LandingPlanCard) => {
    if (isAuthenticated) {
      navigate('/balance')
      return
    }
    handleRegisterCta(`pricing_${plan.key}`)
  }

  useEffect(() => {
    if (!mobileMenuOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [mobileMenuOpen])

  useEffect(() => {
    initLandingAnalytics()
    applyLandingSeo({
      page: 'landing',
      title: t('landing.seo.title'),
      description: t('landing.seo.description'),
      path: '/',
    })
  }, [t])

  useEffect(useLandingDarkThemeEffect(), [])
  useEffect(() => {
    document.documentElement.lang = 'en-US'
  }, [])

  return (
    <div className="kmin bg-[#07070A] text-white min-h-screen font-sans overflow-hidden">

      {/* Background Glow */}
      <div className="kmin-glow">
        <div className="kmin-glow-top" />
        <div className="kmin-glow-bottom" />
      </div>

      {/* ═══════ NAVBAR ═══════ */}
      <header className="kmin-nav">
        <div className="kmin-container">
          <div className="kmin-nav-inner">
            <img className="kmin-logo-img" src={PRODUCT_LOGO_SRC} alt={PRODUCT_NAME} />
            <nav className="kmin-nav-links" aria-label={t('landing.nav.ariaMain')}>
              <a href="#features" onClick={() => setMobileMenuOpen(false)}>{t('landing.nav.features')}</a>
              <a href="#examples" onClick={() => setMobileMenuOpen(false)}>{t('landing.nav.examples')}</a>
              <a href="#assistant" onClick={() => setMobileMenuOpen(false)}>{t('landing.nav.assistant')}</a>
              <a href="#pricing" onClick={() => setMobileMenuOpen(false)}>{t('landing.nav.pricing')}</a>
              <a href="#contacts" onClick={() => setMobileMenuOpen(false)}>{t('landing.nav.contacts')}</a>
            </nav>
            <div className="kmin-nav-actions">
              <button
                type="button"
                className="kmin-nav-burger"
                aria-label={mobileMenuOpen ? t('landing.nav.closeMenu') : t('landing.nav.openMenu')}
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen((v) => !v)}
              >
                <span className="material-symbols-outlined">
                  {mobileMenuOpen ? 'close' : 'menu'}
                </span>
              </button>
              <button className="kmin-btn-primary kmin-nav-cta" onClick={() => handleExploreCta('nav_start')}>
                {t('landing.hero.exploreApp')}
              </button>
            </div>
          </div>
        </div>
        {mobileMenuOpen && (
          <>
            <button
              type="button"
              className="kmin-nav-mobile-backdrop"
              aria-label={t('landing.nav.closeMenu')}
              onClick={() => setMobileMenuOpen(false)}
            />
            <nav className="kmin-nav-mobile-sheet" aria-label={t('landing.nav.ariaMobile')}>
              <a href="#features" onClick={() => setMobileMenuOpen(false)}>{t('landing.nav.features')}</a>
              <a href="#examples" onClick={() => setMobileMenuOpen(false)}>{t('landing.nav.examples')}</a>
              <a href="#assistant" onClick={() => setMobileMenuOpen(false)}>{t('landing.nav.assistant')}</a>
              <a href="#pricing" onClick={() => setMobileMenuOpen(false)}>{t('landing.nav.pricing')}</a>
              <a href="#contacts" onClick={() => setMobileMenuOpen(false)}>{t('landing.nav.contacts')}</a>
              <button
                type="button"
                className="kmin-btn-primary kmin-btn-block"
                onClick={() => {
                  setMobileMenuOpen(false)
                  handleExploreCta('nav_mobile_sheet')
                }}
              >
                {t('landing.hero.exploreApp')}
              </button>
            </nav>
          </>
        )}
      </header>

      {/* ═══════ HERO ═══════ */}
      <section className="kmin-hero">
        <div className="kmin-container">
          <div className="kmin-hero-grid">
            <div>
              <div className="kmin-badge">{t('landing.hero.badge')}</div>
              <h1 className="kmin-hero-title">
                <span className="kmin-hero-accent">
                  {t('landing.hero.titleAccent')}
                </span>
                <span className="block">{t('landing.hero.titleRest')}</span>
              </h1>
              <p className="kmin-hero-desc kmin-hero-desc--accent">
                {t('landing.hero.desc')}
              </p>
              <div className="kmin-hero-actions">
                <button className="kmin-btn-primary kmin-btn-lg" onClick={() => handleExploreCta('hero_primary')}>{t('landing.hero.exploreApp')}</button>
                <button className="kmin-btn-outline kmin-btn-lg" onClick={() => handleRegisterCta('hero_secondary')}>{t('landing.hero.signUpFree')}</button>
              </div>
            </div>

            {/* Hero Preview */}
            <div className="kmin-hero-preview">
              <div className="kmin-hero-preview-glow" />
              <div className="kmin-hero-preview-card">
                <div className="kmin-preview-header">
                  <div>
                    <div className="font-medium">{t('landing.preview.studio')}</div>
                    <div className="kmin-preview-sub">{t('landing.preview.creation')}</div>
                  </div>
                  <div className="kmin-preview-status">{t('landing.preview.online')}</div>
                </div>
                <div className="kmin-preview-prompt">
                  {t('landing.preview.prompt')}
                </div>
                <div className="kmin-preview-grid2">
                  <div className="kmin-preview-box" onClick={() => setPreviewUrl(LANDING_HERO_VIDEO)}>
                    <video
                      src={LANDING_HERO_VIDEO}
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="kmin-preview-img"
                      title={t('landing.preview.videoTitle')}
                    />
                  </div>
                  <div className="kmin-preview-box" onClick={() => setPreviewUrl(libImgs[1])}><img src={libImgs[1]} alt={t('landing.preview.creativeAlt')} className="kmin-preview-img" fetchPriority="high" /></div>
                </div>
                <div className="kmin-preview-grid3">
                  {[2, 3, 4].map((i) => (
                    <div key={i} className="kmin-preview-box-sm" onClick={() => setPreviewUrl(libImgs[i])}><img src={libImgs[i]} alt={t('landing.preview.productAlt')} className="kmin-preview-img-sm" /></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ LOGOS ═══════ */}
      <section className="kmin-logos">
        <div className="kmin-container">
          <div className="kmin-logos-inner">
            {logoStrip.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ EXAMPLES ═══════ */}
      <section id="examples" className="kmin-section kmin-section-alt">
        <div className="kmin-container">
          <div className="kmin-examples-header">
            <div>
              <div className="kmin-label">{t('landing.examples.label')}</div>
              <h3 className="kmin-section-title">{t('landing.examples.title')} <span className="block text-white/70">{t('landing.examples.titleSub')}</span></h3>
            </div>
            <p className="kmin-section-desc" style={{ maxWidth: 500 }}>
              {t('landing.examples.desc')}
            </p>
          </div>
          <div className="kmin-examples-grid">
            {examples.map((item, ei) => {
              const media = LANDING_EXAMPLES[ei]
              return (
              <div key={item} className="kmin-example-card">
                <div className="kmin-example-img">
                  {media.kind === 'video' ? (
                    <video
                      src={media.src}
                      poster={media.poster}
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="kmin-preview-img"
                      title={ei === 0 ? t('landing.examples.videoAvatarTitle') : t('landing.examples.adVideoTitle')}
                      onClick={() => setPreviewUrl(media.src)}
                    />
                  ) : (
                    <img
                      src={media.src}
                      alt={t('landing.examples.adCreativeAlt')}
                      className="kmin-preview-img"
                      loading="lazy"
                      onClick={() => setPreviewUrl(media.src)}
                    />
                  )}
                </div>
                <div className="kmin-example-body">
                  <div className="kmin-example-meta">
                    <span>AI Generated</span>
                    <span className="text-emerald-400">PRO</span>
                  </div>
                  <h4 className="kmin-feature-title">
                    {exampleMeta[ei].title}
                  </h4>
                  <p className="kmin-example-desc">
                    {exampleMeta[ei].desc}
                  </p>
                </div>
              </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ═══════ FEATURES ═══════ */}
      <section id="features" className="kmin-section">
        <div className="kmin-container">
          <div className="kmin-section-header">
            <div className="kmin-label">{t('landing.features.label')}</div>
            <h3 className="kmin-section-title">{t('landing.features.title')} <span className="block text-white/70">{t('landing.features.titleSub')}</span></h3>
            <p className="kmin-section-desc" style={{ color: '#a7fc00' }}>{t('landing.features.desc')}</p>
          </div>
          <div className="kmin-features-grid">
            {features.map((item, fi) => (
              <div key={item.title} className="kmin-feature-card">
                <div className="kmin-feature-img" onClick={() => setPreviewUrl(libImgs[5 + fi])}><img src={libImgs[5 + fi]} alt={t('landing.preview.sectionAlt', { title: item.title })} className="kmin-preview-img" loading="lazy" /></div>
                <div className="kmin-feature-body">
                  <h4 className="kmin-feature-title">{item.title}</h4>
                  <p className="kmin-feature-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ ASSISTANT ═══════ */}
      <section id="assistant" className="kmin-section">
        <div className="kmin-container">
          <div className="kmin-assistant-grid">
            <div>
              <div className="kmin-label">{t('landing.assistant.label')}</div>
              <h3 className="kmin-section-title">{t('landing.assistant.title')} <span className="block text-white/70">{t('landing.assistant.titleSub')}</span></h3>
              <p className="kmin-section-desc" style={{ maxWidth: 440, marginBottom: 40 }}>
                {t('landing.assistant.desc')}
              </p>
              <div className="kmin-usecases">
                {useCases.map(item => (
                  <div key={item} className="kmin-usecase-item">{item}</div>
                ))}
              </div>
            </div>

            {/* Chat Preview */}
            <div className="kmin-chat-card">
              <div className="kmin-chat-header">
                <div>
                  <div className="font-medium">{t('landing.assistant.label')}</div>
                  <div className="kmin-preview-sub">{t('landing.assistant.subtitle')}</div>
                </div>
                <div className="kmin-chat-badge">PRO</div>
              </div>
              <div className="kmin-chat-body">
                <div className="kmin-chat-user">
                  {t('landing.assistant.userMessage')}
                </div>
                <div className="kmin-chat-ai">
                  {t('landing.assistant.recommend')}
                  <div className="mt-4 space-y-3 text-sm">
                    <div className="kmin-chat-suggestion">{t('landing.assistant.suggestion1')}</div>
                    <div className="kmin-chat-suggestion">{t('landing.assistant.suggestion2')}</div>
                    <div className="kmin-chat-suggestion">{t('landing.assistant.suggestion3')}</div>
                  </div>
                  <div className="kmin-chat-actions">
                    <button
                      type="button"
                      className="kmin-btn-primary kmin-btn-sm"
                      onClick={() => handleRegisterCta('assistant_create_banner')}
                    >
                      {t('landing.assistant.createBanner')}
                    </button>
                    <button
                      type="button"
                      className="kmin-btn-ghost kmin-btn-sm"
                      onClick={() => handleRegisterCta('assistant_infographic')}
                    >
                      {t('landing.assistant.createInfographic')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ PRICING ═══════ */}
      <section id="pricing" className="kmin-section kmin-section-alt">
        <div className="kmin-container">
          <div className="kmin-section-header">
            <div className="kmin-label">{t('landing.pricing.label')}</div>
            <h3 className="kmin-section-title">{t('landing.pricing.title')}</h3>
            <p className="kmin-section-desc">{t('landing.pricing.desc')}</p>
          </div>
          <div className="kmin-pricing-grid">
            {landingPlans.map(plan => (
              <div key={plan.key} className={`kmin-plan-card ${plan.accent ? 'kmin-plan-card--accent' : ''}`}>
                <div className="kmin-plan-name">{plan.title}</div>
                <div className="kmin-plan-price">{plan.price}</div>
                <p className="kmin-plan-desc">{plan.desc}</p>
                <button
                  className={plan.accent ? 'kmin-btn-primary kmin-btn-block' : 'kmin-btn-outline kmin-btn-block'}
                  onClick={() => handlePricingCta(plan)}
                >
                  {isAuthenticated ? t('landing.pricing.selectPlan') : t('landing.pricing.select')}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ CONTACTS ═══════ */}
      <section id="contacts" className="kmin-section">
        <div className="kmin-container">
          <div className="kmin-contacts-card">
            <div className="kmin-contacts-glow" />
            <div className="kmin-contacts-content">
              <div className="kmin-section-header" style={{ marginBottom: 48 }}>
                <div className="kmin-label">{t('landing.contacts.label')}</div>
                <h3 className="kmin-section-title">
                  {t('landing.contacts.title')}
                  <span className="block text-white/70">{t('landing.contacts.titleSub')}</span>
                </h3>
                <p className="kmin-section-desc">
                  {t('landing.contacts.desc')}
                </p>
              </div>
              <div className="kmin-contacts-grid kmin-contacts-grid--single">
                <a
                  href={`mailto:${LANDING_CONTACT_EMAIL}`}
                  className="kmin-contact-item"
                  onClick={() => trackLandingCta('landing', 'contacts_email')}
                >
                  <span className="kmin-contact-icon material-symbols-outlined" aria-hidden="true">mail</span>
                  <div className="kmin-contact-text">
                    <span className="kmin-contact-label">{t('landing.contacts.emailLabel')}</span>
                    <span className="kmin-contact-value">{LANDING_CONTACT_EMAIL}</span>
                  </div>
                  <span className="kmin-contact-arrow material-symbols-outlined" aria-hidden="true">arrow_outward</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ FOOTER ═══════ */}
      <footer className="kmin-footer">
        <div className="kmin-container">
          <div className="kmin-footer-inner">
            <div>
              <img className="kmin-logo-img kmin-logo-img--footer" src={PRODUCT_LOGO_SRC} alt={PRODUCT_NAME} style={{ margin: 0 }} />
              <div className="kmin-footer-sub">{t('landing.footer.sub')}</div>
              <div className="kmin-footer-legal">
                <div>{t('landing.footer.legalName')}</div>
                <div>{t('landing.footer.inn')}</div>
                <div>{t('landing.footer.ogrnip')}</div>
              </div>
            </div>
            <div className="kmin-footer-links">
              <Link to="/public-offer" className="kmin-footer-link-btn">
                {t('landing.footer.terms')}
              </Link>
              <Link to="/privacy" className="kmin-footer-link-btn">
                {t('landing.footer.privacy')}
              </Link>
              <Link to="/cookie-policy" className="kmin-footer-link-btn">
                {t('landing.footer.cookies')}
              </Link>
              <a href="#contacts" className="kmin-footer-link-btn">
                {t('landing.footer.contact')}
              </a>
              <button
                type="button"
                className="kmin-footer-link-btn"
                onClick={() => handleRegisterCta('footer_support')}
              >
                {t('landing.footer.support')}
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Image preview overlay */}
      {previewUrl && (
        <div className="kmin-preview-overlay" onClick={() => setPreviewUrl(null)}>
          <button className="kmin-preview-close" onClick={() => setPreviewUrl(null)}>
            <span className="material-symbols-outlined">close</span>
          </button>
          {previewUrl.endsWith('.mp4') ? (
            <video src={previewUrl} autoPlay loop playsInline className="kmin-preview-full" controls onClick={e => e.stopPropagation()} />
          ) : (
            <img src={previewUrl} alt={t('landing.preview.enlargedAlt')} className="kmin-preview-full" />
          )}
        </div>
      )}
    </div>
  )
}
